/**
 * Export utilities for keyword search results.
 * Generates CSV files from search results for download.
 */
import Papa from 'papaparse';
import type { KeywordSearchResultItem } from '../types';

// ─── URL Builders ────────────────────────────────────────────────────────────

function buildDailyMedUrl(setId: string | null): string {
  if (!setId) return '';
  return `https://dailymed.nlm.nih.gov/dailymed/drugInfo.cfm?setid=${setId}`;
}

function buildEmaUrl(brandName: string | null): string {
  if (!brandName) return '';
  const slug = brandName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  return `https://www.ema.europa.eu/en/medicines/human/EPAR/${slug}`;
}

function buildLabelUrl(result: KeywordSearchResultItem): string {
  if (result.region === 'EU') return buildEmaUrl(result.brand_name);
  return buildDailyMedUrl(result.set_id);
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function stripHtml(html: string): string {
  return html.replace(/<[^>]+>/g, '').trim();
}

function deriveApplicationType(applicationNumber: string | null): string {
  if (!applicationNumber) return '';
  const upper = applicationNumber.trim().toUpperCase();
  if (upper.startsWith('NDA')) return 'NDA';
  if (upper.startsWith('BLA')) return 'BLA';
  if (upper.startsWith('ANDA')) return 'ANDA';
  return 'Other';
}

export function triggerDownload(csvString: string, filename: string): void {
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function getTimestamp(): string {
  const now = new Date();
  return now.toISOString().slice(0, 19).replace(/[T:]/g, '-');
}

// ─── Export: Search Matrix ───────────────────────────────────────────────────

interface MatrixRow {
  'Drug Name': string;
  'Generic Name': string;
  'Manufacturer': string;
  'Region': string;
  'Application Type': string;
  'Total Matches': number;
  'Label URL': string;
}

export function exportSearchMatrix(results: KeywordSearchResultItem[]): void {
  const data: MatrixRow[] = results.map((r) => ({
    'Drug Name': r.brand_name || 'Unknown',
    'Generic Name': r.generic_name || '',
    'Manufacturer': r.manufacturer || '',
    'Region': r.region,
    'Application Type': deriveApplicationType(r.application_number),
    'Total Matches': r.total_matches,
    'Label URL': buildLabelUrl(r),
  }));

  const csv = Papa.unparse(data);
  triggerDownload(csv, `search-matrix-${getTimestamp()}.csv`);
}

// ─── Export: Detailed Matches ────────────────────────────────────────────────

interface DetailedRow {
  'Drug Name': string;
  'Generic Name': string;
  'Manufacturer': string;
  'Region': string;
  'Section': string;
  'Section Number': string;
  'Match Text': string;
  'Rank': number;
  'Label URL': string;
}

export function exportDetailedMatches(results: KeywordSearchResultItem[]): void {
  const data: DetailedRow[] = [];

  for (const r of results) {
    const url = buildLabelUrl(r);
    for (const hl of r.highlights) {
      data.push({
        'Drug Name': r.brand_name || 'Unknown',
        'Generic Name': r.generic_name || '',
        'Manufacturer': r.manufacturer || '',
        'Region': r.region,
        'Section': hl.section_title || '',
        'Section Number': hl.section_number || '',
        'Match Text': stripHtml(hl.headline),
        'Rank': Math.round(hl.rank * 1000) / 1000,
        'Label URL': url,
      });
    }
  }

  const csv = Papa.unparse(data);
  triggerDownload(csv, `search-detailed-${getTimestamp()}.csv`);
}
